import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Grid, 
    List, 
    Layout, 
    TrendingDown, 
    CheckCircle, 
    Clock, 
    AlertCircle,
    BarChart,
    ChevronDown,
    ChevronUp,
    Tag,
    Filter,
    Search,
    DollarSign,
    FileText,
    Package,
    Activity,
    Link,
    Circle
} from 'react-feather';
import api, { taskApi, featureTagApi, projectApi, releasesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { IconButton } from '../components/ui/IconButton';
import { FeatherArrowLeft } from '../components/ui/icons';
import { isManager } from '../utils/roleUtils';
import TaskForm from '../components/Tasks/TaskForm';
import TaskList from '../components/Tasks/TaskList';
import TagBar from '../components/FeatureTags/TagBar';
import FeatureTagStats from '../components/FeatureTags/FeatureTagStats';
import TaskFeatureTimeline from '../components/FeatureTags/TaskFeatureTimeline';
import TaskSummaryModal from './components/TaskSummaryModal';
import BillingTab from '../components/Projects/BillingTab';
import ReleaseManagementStatsTab from '../components/Projects/ReleaseManagementStatsTab';
import ProjectNotesArtifacts from '../components/Projects/ProjectNotesArtifacts';
import TaskTypeSelect from '../components/shared/TaskTypeSelect';
import { DEFAULT_TASK_TYPE, TASK_TYPE_SUBTASK } from '../constants/taskTypes';
import './ProjectDetails.css';

/** Build tree-ordered task list: roots first, each root followed by its subtasks (from linked_tasks with direction 'subtask'). */
function buildTaskTreeOrder(tasks) {
    const subtaskIds = new Set();
    tasks.forEach(task => {
        (task.linked_tasks || []).forEach(link => {
            if (link.direction === 'subtask' && link.linked_task?.id) subtaskIds.add(link.linked_task.id);
        });
    });
    const roots = tasks.filter(t => !subtaskIds.has(t.id));
    const ordered = [];
    roots.forEach(root => {
        ordered.push(root);
        (root.linked_tasks || []).forEach(link => {
            if (link.direction === 'subtask' && link.linked_task) {
                const full = tasks.find(t => t.id === link.linked_task.id);
                if (full) ordered.push(full);
            }
        });
    });
    return { ordered, subtaskIds };
}

const ProjectDetails = () => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [users, setUsers] = useState([]);
    const [featureTags, setFeatureTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showTaskForm, setShowTaskForm] = useState(false);
    const [createSubtaskForTaskId, setCreateSubtaskForTaskId] = useState(null);
    const { user } = useAuth();
    const [userIsManager, setUserIsManager] = useState(false);
    
    // New state for enhanced functionality
    const [viewMode, setViewMode] = useState('compact'); // 'grid', 'list', or 'compact'
    const [subtaskBubblesMode, setSubtaskBubblesMode] = useState(false);
    const [filters, setFilters] = useState({
        status: [], // Changed to array for multiselect
        createdBy: '',
        assignedTo: '',
        taskType: '',
        withLinksOnly: false
    });
    const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTagId, setSelectedTagId] = useState(null);
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
    const [statsCollapsed, setStatsCollapsed] = useState(true);
    const [tagBarCollapsed, setTagBarCollapsed] = useState(true);
    const [taskControlsCollapsed, setTaskControlsCollapsed] = useState(false);
    const [featureTagStatsCollapsed, setFeatureTagStatsCollapsed] = useState(true);
    const [taskTimelineCollapsed, setTaskTimelineCollapsed] = useState(true);
    const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
    const [activeTab, setActiveTab] = useState('tasks'); // 'tasks', 'billing', 'releases', or 'notes-artifacts'
    const [groupingMode, setGroupingMode] = useState('none'); // 'none', 'feature-tag', 'work-plan', 'releases', 'task-links'
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [todayWorkPlans, setTodayWorkPlans] = useState([]);
    const [releases, setReleases] = useState([]);
    const statusDropdownRef = useRef(null);
    const taskFormTitleRef = useRef(null);

    // Helper function to calculate task summary data
    const calculateTaskSummary = useCallback((task) => {
        if (!task.work_plan_tasks || task.work_plan_tasks.length === 0) {
            return {
                totalHours: 0,
                occurrences: 0,
                work_plan_attachments: []
            };
        }

        const totalHours = task.work_plan_tasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
        const occurrences = task.work_plan_tasks.length;
        
        // Collect all attachments from work plan tasks
        const attachments = [];
        task.work_plan_tasks.forEach(wpt => {
            if (wpt.work_plan_attachments && wpt.work_plan_attachments.length > 0) {
                attachments.push(...wpt.work_plan_attachments);
            }
        });

        return {
            totalHours: totalHours.toFixed(1),
            occurrences,
            work_plan_attachments: attachments
        };
    }, []);

    // Function to open task summary modal
    const openTaskSummaryModal = useCallback((task) => {
        const summary = calculateTaskSummary(task);
        setSelectedTaskForModal({
            ...task,
            ...summary
        });
    }, [calculateTaskSummary]);

    // Function to close task summary modal
    const closeTaskSummaryModal = useCallback(() => {
        setSelectedTaskForModal(null);
    }, []);

    // Helper function to get task status
    const getTaskStatus = useCallback((taskId) => {
        const task = tasks.find(t => t.id === taskId);
        return task?.status || 'open';
    }, [tasks]);

    // Helper function to get status variant for badge
    const getStatusVariant = useCallback((status) => {
        switch (status?.toLowerCase()) {
            case 'in-progress':
                return 'warning';
            case 'completed':
                return 'success';
            case 'released':
                return 'success';
            case 'open':
                return 'neutral';
            default:
                return 'neutral';
        }
    }, []);

    // Helper function to get task history from work_plan_tasks
    const getTaskHistory = useCallback((taskId) => {
        const task = tasks.find(t => t.id === taskId);
        if (!task || !task.work_plan_tasks || task.work_plan_tasks.length === 0) {
            return [];
        }

        return task.work_plan_tasks
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map(wpt => {
                const dateObj = new Date(wpt.created_at);
                return {
                    date: dateObj, // Store Date object instead of string
                    hours: wpt.hours || 0,
                    notes: wpt.notes || '',
                    status: wpt.status === 'completed' ? 'Completed' : 'In Progress',
                    attachments: wpt.work_plan_attachments || []
                };
            });
    }, [tasks]);

    // Function to recalculate tag counts based on current tasks
    const recalculateTagCounts = useCallback(() => {
        setFeatureTags(prev => prev.map(tag => {
            const count = tasks.filter(task => task.feature_tag?.id === tag.id).length;
            return { ...tag, task_count: count };
        }));
    }, [tasks]);


    // Recalculate tag counts whenever tasks change
    useEffect(() => {
        if (tasks.length > 0 && featureTags.length > 0) {
            recalculateTagCounts();
        }
    }, [recalculateTagCounts, tasks.length]);

    const checkUserRole = useCallback(async () => {
        try {
            const response = await api.get('/api/auth/role');
            const userRole = response.data?.role;
            setUserIsManager(isManager(userRole));
        } catch (err) {
            console.error('Error checking user role:', err);
            setError('Failed to check user permissions');
        }
    }, []);

    const fetchProjectData = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch project details
            const projectResponse = await api.get(`/api/projects/${projectId}`);
            if (!projectResponse.data) {
                navigate('/projects');
                return;
            }
            setProject(projectResponse.data);

            // Fetch project tasks
            const tasksResponse = await api.get(`/api/tasks/project/${projectId}`);
            setTasks(tasksResponse.data || []);
            
            
            setError('');
        } catch (err) {
            console.error('Error fetching project data:', err);
            if (err.response?.status === 404) {
                navigate('/projects');
                return;
            }
            setError('Failed to fetch project data');
        } finally {
            setLoading(false);
        }
    }, [projectId, navigate]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await api.get('/api/users');
            setUsers(response.data || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    }, []);

    const fetchFeatureTags = useCallback(async () => {
        try {
            const data = await featureTagApi.getByProject(projectId);
            setFeatureTags(data || []);
        } catch (err) {
            console.error('Error fetching feature tags:', err);
        }
    }, [projectId]);

    useEffect(() => {
        if (!projectId) {
            navigate('/projects');
            return;
        }

        if (user?.id) {
            checkUserRole();
            fetchProjectData();
            fetchUsers();
            fetchFeatureTags();
        }
    }, [projectId, user?.id, navigate, fetchProjectData, fetchUsers, fetchFeatureTags, checkUserRole]);

    // Calculate task statistics
    const taskStats = useMemo(() => {
        const total = tasks.length;
        const open = tasks.filter(task => task.status === 'open').length;
        const inProgress = tasks.filter(task => task.status === 'in-progress').length;
        const completed = tasks.filter(task => task.status === 'completed').length;
        const released = tasks.filter(task => task.status === 'released').length;
        const completionPercentage = total > 0 ? Math.round(((completed + released) / total) * 100) : 0;
        
        return {
            total,
            open,
            inProgress,
            completed,
            released,
            completionPercentage
        };
    }, [tasks]);

    // Filter out users with role "unknown"
    const filteredUsers = useMemo(() => {
        return users.filter(user => user.role !== 'unknown');
    }, [users]);

    // Filter and sort tasks
    const filteredAndSortedTasks = useMemo(() => {
        let filtered = tasks;

        // Apply filters
        if (filters.status && filters.status.length > 0) {
            filtered = filtered.filter(task => filters.status.includes(task.status));
        }
        if (filters.createdBy) {
            filtered = filtered.filter(task => task.created_by?.id === filters.createdBy);
        }
        if (filters.assignedTo) {
            if (filters.assignedTo === 'unassigned') {
                filtered = filtered.filter(task => !task.assigned_to?.id);
            } else {
                filtered = filtered.filter(task => task.assigned_to?.id === filters.assignedTo);
            }
        }
        if (filters.taskType) {
            filtered = filtered.filter(task => (task.task_type || DEFAULT_TASK_TYPE) === filters.taskType);
        }
        
        // Apply tag filter
        if (selectedTagId !== null) {
            if (selectedTagId === 'untagged') {
                filtered = filtered.filter(task => !task.feature_tag?.id);
            } else {
                filtered = filtered.filter(task => task.feature_tag?.id === selectedTagId);
            }
        }

        // Apply search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(task => {
                const title = (task.title || '').toLowerCase();
                const description = (task.description || '').toLowerCase();
                const displayId = (task.display_id || '').toLowerCase();
                const notes = (task.notes || '').toLowerCase();
                const assignedToName = (task.assigned_to?.display_name || task.assigned_to?.email || '').toLowerCase();
                const createdByName = (task.created_by?.display_name || task.created_by?.email || '').toLowerCase();
                const tagName = (task.feature_tag?.name || '').toLowerCase();
                
                return title.includes(query) ||
                       description.includes(query) ||
                       displayId.includes(query) ||
                       notes.includes(query) ||
                       assignedToName.includes(query) ||
                       createdByName.includes(query) ||
                       tagName.includes(query);
            });
        }

        // Only tasks with links (when not in task-links grouping)
        if (filters.withLinksOnly) {
            filtered = filtered.filter(task => (task.linked_tasks?.length ?? 0) > 0);
        }

        // Sort tasks by creation date
        filtered.sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });

        return filtered;
    }, [tasks, filters, sortOrder, selectedTagId, searchQuery]);

    // Tree order: subtasks appear under their parent Story/Task
    const { ordered: tasksInTreeOrder, subtaskIds } = useMemo(
        () => buildTaskTreeOrder(filteredAndSortedTasks),
        [filteredAndSortedTasks]
    );

    // Helper function to check if feature tag is overdue
    const isFeatureOverdue = useCallback((featureTag) => {
        if (!featureTag?.due_date) return false;
        return new Date(featureTag.due_date) < new Date();
    }, []);

    // Fetch today's work plans for the project
    const fetchTodayWorkPlans = useCallback(async () => {
        try {
            const data = await projectApi.getTodayWorkPlans(projectId);
            setTodayWorkPlans(data || []);
        } catch (err) {
            console.error('Error fetching today\'s work plans:', err);
            setTodayWorkPlans([]);
        }
    }, [projectId]);

    // Fetch releases for the project
    const fetchReleases = useCallback(async () => {
        try {
            const data = await releasesApi.getProjectReleases(projectId);
            setReleases(data || []);
        } catch (err) {
            console.error('Error fetching releases:', err);
            setReleases([]);
        }
    }, [projectId]);

    // Fetch work plans when project loads or grouping mode changes to work-plan
    useEffect(() => {
        if (projectId && groupingMode === 'work-plan') {
            fetchTodayWorkPlans();
        }
    }, [projectId, groupingMode, fetchTodayWorkPlans]);

    // Fetch releases when project loads (needed for release badges on tasks)
    useEffect(() => {
        if (projectId) {
            fetchReleases();
        }
    }, [projectId, fetchReleases]);

    // Group tasks by feature tag
    const groupedTasks = useMemo(() => {
        if (groupingMode !== 'feature-tag') {
            return { 'all': { tag: null, tasks: tasksInTreeOrder } };
        }

        const groups = {};
        filteredAndSortedTasks.forEach(task => {
            const tagId = task.feature_tag?.id || 'untagged';
            if (!groups[tagId]) {
                // Use the latest tag data from featureTags array instead of task's embedded tag
                const latestTag = featureTags.find(tag => tag.id === tagId);
                groups[tagId] = {
                    tag: latestTag || task.feature_tag || { name: 'Untagged', color: '#6B7280', id: 'untagged' },
                    tasks: []
                };
            }
            groups[tagId].tasks.push(task);
        });

        // Sort tasks within each group by created date (respecting sortOrder), then apply tree order
        Object.values(groups).forEach(group => {
            group.tasks.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            });
            group.tasks = buildTaskTreeOrder(group.tasks).ordered;
        });

        return groups;
    }, [filteredAndSortedTasks, groupingMode, sortOrder, featureTags, tasksInTreeOrder]);

    // Group tasks by work plan (by user)
    const groupedTasksByWorkPlan = useMemo(() => {
        if (groupingMode !== 'work-plan') {
            return { 'all': { user: null, tasks: tasksInTreeOrder } };
        }

        // Create a map of task_id -> user_id(s) from work plans
        const taskToUsersMap = new Map();
        todayWorkPlans.forEach(wp => {
            wp.task_ids?.forEach(taskId => {
                if (!taskToUsersMap.has(taskId)) {
                    taskToUsersMap.set(taskId, []);
                }
                taskToUsersMap.get(taskId).push(wp.user_id);
            });
        });

        const groups = {};
        const tasksInWorkPlan = new Set();

        // Group tasks by user
        filteredAndSortedTasks.forEach(task => {
            const userIds = taskToUsersMap.get(task.id) || [];
            if (userIds.length > 0) {
                tasksInWorkPlan.add(task.id);
                // Add task to each user's group (task can be in multiple work plans)
                userIds.forEach(userId => {
                    if (!groups[userId]) {
                        const workPlan = todayWorkPlans.find(wp => wp.user_id === userId);
                        groups[userId] = {
                            user: workPlan?.user || { id: userId, display_name: 'Unknown', email: '' },
                            tasks: []
                        };
                    }
                    // Only add task once per user group
                    if (!groups[userId].tasks.find(t => t.id === task.id)) {
                        groups[userId].tasks.push(task);
                    }
                });
            }
        });

        // Add "Not in Work Plan" group
        const notInWorkPlanTasks = filteredAndSortedTasks.filter(task => !tasksInWorkPlan.has(task.id));
        if (notInWorkPlanTasks.length > 0) {
            groups['not-in-work-plan'] = {
                user: { id: 'not-in-work-plan', display_name: 'Not in Work Plan', email: '' },
                tasks: notInWorkPlanTasks
            };
        }

        // Sort tasks within each group by created date (respecting sortOrder), then apply tree order
        Object.values(groups).forEach(group => {
            group.tasks.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            });
            group.tasks = buildTaskTreeOrder(group.tasks).ordered;
        });

        return groups;
    }, [filteredAndSortedTasks, groupingMode, sortOrder, todayWorkPlans, tasksInTreeOrder]);

    // Group tasks by releases (through feature tags)
    const groupedTasksByReleases = useMemo(() => {
        if (groupingMode !== 'releases') {
            return { 'all': { release: null, tasks: tasksInTreeOrder } };
        }

        // Create a map of feature_tag_id -> release_id(s)
        const featureTagToReleasesMap = new Map();
        releases.forEach(release => {
            const releaseFeatureTags = release.release_feature_tags || [];
            releaseFeatureTags.forEach(rft => {
                const featureTagId = rft.feature_tag?.id;
                if (featureTagId) {
                    if (!featureTagToReleasesMap.has(featureTagId)) {
                        featureTagToReleasesMap.set(featureTagId, []);
                    }
                    featureTagToReleasesMap.get(featureTagId).push(release.id);
                }
            });
        });

        const groups = {};
        const tasksInReleases = new Set();

        // Group tasks by release
        filteredAndSortedTasks.forEach(task => {
            const featureTagId = task.feature_tag?.id;
            if (featureTagId && featureTagToReleasesMap.has(featureTagId)) {
                const releaseIds = featureTagToReleasesMap.get(featureTagId);
                releaseIds.forEach(releaseId => {
                    tasksInReleases.add(task.id);
                    if (!groups[releaseId]) {
                        const release = releases.find(r => r.id === releaseId);
                        groups[releaseId] = {
                            release: release || { id: releaseId, name: 'Unknown Release', version: '', status: 'planned' },
                            tasks: []
                        };
                    }
                    // Only add task once per release group
                    if (!groups[releaseId].tasks.find(t => t.id === task.id)) {
                        groups[releaseId].tasks.push(task);
                    }
                });
            }
        });

        // Add "Not in Release" group
        const notInReleaseTasks = filteredAndSortedTasks.filter(task => !tasksInReleases.has(task.id));
        if (notInReleaseTasks.length > 0) {
            groups['not-in-release'] = {
                release: { id: 'not-in-release', name: 'Not in Release', version: '', status: 'planned' },
                tasks: notInReleaseTasks
            };
        }

        // Sort tasks within each group by created date (respecting sortOrder), then apply tree order
        Object.values(groups).forEach(group => {
            group.tasks.sort((a, b) => {
                const dateA = new Date(a.created_at);
                const dateB = new Date(b.created_at);
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
            });
            group.tasks = buildTaskTreeOrder(group.tasks).ordered;
        });

        return groups;
    }, [filteredAndSortedTasks, groupingMode, sortOrder, releases, tasksInTreeOrder]);

    // Group tasks by task links: tasks with outgoing links (depends_on, blocked_by, duplicates, subtask) or incoming (parent_of) are "with links"
    const groupedTasksByLinks = useMemo(() => {
        const linkedDirections = ['depends_on', 'blocked_by', 'duplicates', 'subtask', 'parent_of'];
        const hasAnyLink = (d) => linkedDirections.includes((d || '').toLowerCase());
        if (groupingMode !== 'task-links') {
            return { 'all': { withLinks: null, tasks: tasksInTreeOrder } };
        }
        const withLinks = filteredAndSortedTasks.filter(task => {
            const links = task.linked_tasks || [];
            if (links.length === 0) return false;
            return links.some(link => hasAnyLink(link.direction || link.relationship_type));
        });
        const withoutLinks = filteredAndSortedTasks.filter(task => {
            const links = task.linked_tasks || [];
            if (links.length === 0) return true;
            return !links.some(link => hasAnyLink(link.direction || link.relationship_type));
        });
        const groups = {};
        if (withLinks.length > 0) {
            groups['with-links'] = { withLinks: true, tasks: withLinks };
        }
        if (withoutLinks.length > 0) {
            groups['without-links'] = { withLinks: false, tasks: buildTaskTreeOrder(withoutLinks).ordered };
        }
        if (withLinks.length > 0) {
            groups['with-links'].tasks = buildTaskTreeOrder(withLinks).ordered;
        }
        return groups;
    }, [filteredAndSortedTasks, groupingMode]);

    // Initialize expanded groups when grouping is enabled
    useEffect(() => {
        if (groupingMode === 'feature-tag' && Object.keys(groupedTasks).length > 0) {
            setExpandedGroups(new Set(Object.keys(groupedTasks)));
        } else if (groupingMode === 'work-plan' && Object.keys(groupedTasksByWorkPlan).length > 0) {
            setExpandedGroups(new Set(Object.keys(groupedTasksByWorkPlan)));
        } else if (groupingMode === 'releases' && Object.keys(groupedTasksByReleases).length > 0) {
            setExpandedGroups(new Set(Object.keys(groupedTasksByReleases)));
        } else if (groupingMode === 'task-links' && Object.keys(groupedTasksByLinks).length > 0) {
            const linkGroupIds = Object.keys(groupedTasksByLinks);
            setExpandedGroups(new Set(linkGroupIds));
        } else if (groupingMode === 'none') {
            setExpandedGroups(new Set());
        }
    }, [groupingMode, groupedTasks, groupedTasksByWorkPlan, groupedTasksByReleases, groupedTasksByLinks]);

    const toggleGroup = useCallback((groupId) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    }, []);

    const handleTaskCreated = async (formData) => {
        try {
            let response;
            // If formData is FormData, add project_id to it
            if (formData instanceof FormData) {
                formData.append('project_id', projectId);
                response = await taskApi.create(formData);
            } else {
                // Handle JSON data case
                response = await taskApi.create({
                    ...formData,
                    project_id: projectId
                });
            }

            // Update tasks state
            setTasks(prev => [...prev, response]);

            const parentId = createSubtaskForTaskId;
            if (parentId) {
                try {
                    const link = await taskApi.createLink(parentId, response.id, 'subtask');
                    setTasks(prev => prev.map(t =>
                        t.id === parentId
                            ? { ...t, linked_tasks: [...(t.linked_tasks || []), link] }
                            : t
                    ));
                } catch (linkErr) {
                    console.error('Error linking subtask:', linkErr);
                }
                setCreateSubtaskForTaskId(null);
            }

            setShowTaskForm(false);
        } catch (error) {
            console.error('Error creating task:', error);
        }
    };

    const handleTaskStatusChange = async (taskId, newStatus) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}/status`, {
                status: newStatus
            });
            setTasks(prev => prev.map(task => 
                task.id === taskId ? response.data : task
            ));
        } catch (err) {
            console.error('Error updating task status:', err);
        }
    };

    const handleAssigneeChange = async (taskId, newAssigneeId) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}`, {
                assigned_to: newAssigneeId
            });
            setTasks(prev => prev.map(task => 
                task.id === taskId ? response.data : task
            ));
        } catch (err) {
            console.error('Error updating task assignee:', err);
        }
    };

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
    };

    const handleStatusToggle = (status) => {
        setFilters(prev => {
            const currentStatuses = prev.status || [];
            const newStatuses = currentStatuses.includes(status)
                ? currentStatuses.filter(s => s !== status)
                : [...currentStatuses, status];
            return { ...prev, status: newStatuses };
        });
    };

    const statusOptions = [
        { value: 'open', label: 'Open' },
        { value: 'in-progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'released', label: 'Released' }
    ];

    const clearFilters = () => {
        setFilters({
            status: [],
            createdBy: '',
            assignedTo: '',
            taskType: '',
            withLinksOnly: false
        });
        setSelectedTagId(null);
        setSearchQuery('');
        setStatusDropdownOpen(false);
    };

    const toggleSortOrder = () => {
        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    };

    const handleTagUpdate = async (taskId, tagId) => {
        try {
            const response = await api.patch(`/api/tasks/${taskId}`, {
                feature_tag_id: tagId
            });
            setTasks(prev => prev.map(task => 
                task.id === taskId ? response.data : task
            ));
        } catch (err) {
            console.error('Error updating task tag:', err);
        }
    };

    const handleTagCreate = (newTag) => {
        setFeatureTags(prev => {
            // Check if tag already exists to avoid duplicates
            if (prev.find(tag => tag.id === newTag.id)) {
                return prev;
            }
            return [...prev, newTag];
        });
    };

    const handleFeatureTagUpdate = (updatedTag) => {
        setFeatureTags(prev => prev.map(tag => 
            tag.id === updatedTag.id ? updatedTag : tag
        ));
        // Update tasks to reflect the new tag name
        setTasks(prev => prev.map(task => {
            if (task.feature_tag?.id === updatedTag.id) {
                return {
                    ...task,
                    feature_tag: {
                        ...task.feature_tag,
                        name: updatedTag.name,
                        color: updatedTag.color,
                        description: updatedTag.description,
                        due_date: updatedTag.due_date
                    }
                };
            }
            return task;
        }));
    };

    // Calculate untagged count
    const untaggedCount = tasks.filter(task => !task.feature_tag?.id).length;

    // Get selected tag name for display
    const getSelectedTagName = () => {
        if (selectedTagId === null) return null;
        if (selectedTagId === 'untagged') return 'Untagged';
        const selectedTag = featureTags.find(tag => tag.id === selectedTagId);
        return selectedTag?.name || null;
    };

    // Get active filter count and labels
    const getActiveFilters = () => {
        const active = [];
        if (filters.status && filters.status.length > 0) {
            const statusLabels = filters.status.map(status => 
                status.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')
            );
            active.push({ label: 'Status', value: statusLabels.join(', ') });
        }
        if (filters.createdBy) {
            const user = filteredUsers.find(u => u.id === filters.createdBy);
            active.push({ label: 'Created By', value: user?.display_name || user?.email || 'Unknown' });
        }
        if (filters.assignedTo) {
            if (filters.assignedTo === 'unassigned') {
                active.push({ label: 'Assigned To', value: 'Unassigned' });
            } else {
                const user = filteredUsers.find(u => u.id === filters.assignedTo);
                active.push({ label: 'Assigned To', value: user?.display_name || user?.email || 'Unknown' });
            }
        }
        if (filters.taskType) {
            active.push({ label: 'Type', value: filters.taskType });
        }
        return active;
    };

    // Calculate feature tag analytics
    const tagAnalytics = React.useMemo(() => {
        const now = new Date();
        let overdueCount = 0;
        let upcomingCount = 0;
        let totalCompletion = 0;
        let tagsWithTasks = 0;
        let totalHours = 0;
        let completedHours = 0;

        featureTags.forEach(tag => {
            if (tag.due_date) {
                const dueDate = new Date(tag.due_date);
                const daysRemaining = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
                
                if (daysRemaining < 0) {
                    overdueCount++;
                } else if (daysRemaining <= 7) {
                    upcomingCount++;
                }
            }

            // Calculate completion percentage and hours for this tag
            const tagTasks = tasks.filter(task => task.feature_tag?.id === tag.id);
            if (tagTasks.length > 0) {
                const completedTasks = tagTasks.filter(task => task.status === 'completed').length;
                const completionPercentage = (completedTasks / tagTasks.length) * 100;
                totalCompletion += completionPercentage;
                tagsWithTasks++;
                
                // Calculate hours
                const tagHours = tagTasks.reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
                const tagCompletedHours = tagTasks.filter(task => task.status === 'completed').reduce((sum, task) => sum + (parseFloat(task.hours) || 0), 0);
                totalHours += tagHours;
                completedHours += tagCompletedHours;
            }
        });

        const averageCompletion = tagsWithTasks > 0 ? Math.round(totalCompletion / tagsWithTasks) : 0;

        return {
            overdueCount,
            upcomingCount,
            averageCompletion,
            totalTags: featureTags.length,
            totalHours: Math.round(totalHours * 10) / 10,
            completedHours: Math.round(completedHours * 10) / 10
        };
    }, [featureTags, tasks]);

    const handleTagDelete = (tagId) => {
        setFeatureTags(prev => prev.filter(tag => tag.id !== tagId));
        // Refresh tasks to remove the deleted tag from task data
        fetchProjectData();
    };

    // Close status dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
                setStatusDropdownOpen(false);
            }
        };

        if (statusDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [statusDropdownOpen]);

    // Auto-focus title input when task form opens and handle Escape key
    useEffect(() => {
        if (showTaskForm && taskFormTitleRef.current) {
            // Small delay to ensure panel animation has started
            setTimeout(() => {
                taskFormTitleRef.current?.focus();
            }, 100);
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape' && showTaskForm) {
                setShowTaskForm(false);
            }
        };

        if (showTaskForm) {
            document.addEventListener('keydown', handleEscape);
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showTaskForm]);

    // Accordion handlers - only one section visible at a time
    const handleStatsToggle = () => {
        if (statsCollapsed) {
            // Expanding - collapse others
            setFeatureTagStatsCollapsed(true);
            setTaskTimelineCollapsed(true);
        }
        setStatsCollapsed(!statsCollapsed);
    };

    const handleFeatureTagStatsToggle = () => {
        if (featureTagStatsCollapsed) {
            // Expanding - collapse others
            setStatsCollapsed(true);
            setTaskTimelineCollapsed(true);
        }
        setFeatureTagStatsCollapsed(!featureTagStatsCollapsed);
    };

    const handleTaskTimelineToggle = () => {
        if (taskTimelineCollapsed) {
            // Expanding - collapse others
            setStatsCollapsed(true);
            setFeatureTagStatsCollapsed(true);
        }
        setTaskTimelineCollapsed(!taskTimelineCollapsed);
    };

    if (loading) {
        return (
            <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading project details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-red-600">
                {error}
            </div>
        );
    }

    if (!project) {
        return <div className="p-4">Project not found</div>;
    }

    return (
        <div className="project-details">
            <div className="project-header">
                <div className="project-header-top">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <Button
                            variant="neutral-tertiary"
                            icon={<FeatherArrowLeft />}
                            onClick={() => navigate('/projects')}
                            className="back-button"
                        >
                            Back to Projects
                        </Button>
                        <Button
                            variant="neutral-tertiary"
                            onClick={() => navigate(`/projects/${projectId}`)}
                            className="back-button"
                            title="Return to the standard project view (paginated tasks)"
                        >
                            Standard view
                        </Button>
                    </div>
                </div>
                <div className="project-header-content">
                    <div>
                    <h1 className="project-title">{project.name}</h1>
                    <p className="project-description">{project.description}</p>
                    
                    {/* Feature Tag Analytics Summary */}
                    {tagAnalytics.totalTags > 0 && (
                        <div className="project-analytics-summary">
                            <div className="analytics-item">
                                <span className="analytics-label">Features:</span>
                                <span className="analytics-value">{tagAnalytics.totalTags}</span>
                            </div>
                            <div className="analytics-item">
                                <span className="analytics-label">Avg. Progress:</span>
                                <span className="analytics-value">{tagAnalytics.averageCompletion}%</span>
                            </div>
                            <div className="analytics-item">
                                <span className="analytics-label">Total Hours:</span>
                                <span className="analytics-value">{tagAnalytics.totalHours}h</span>
                            </div>
                            <div className="analytics-item">
                                <span className="analytics-label">Completed:</span>
                                <span className="analytics-value">{tagAnalytics.completedHours}h</span>
                            </div>
                            {tagAnalytics.overdueCount > 0 && (
                                <div className="analytics-item overdue">
                                    <span className="analytics-label">Overdue:</span>
                                    <span className="analytics-value">{tagAnalytics.overdueCount}</span>
                                </div>
                            )}
                            {tagAnalytics.upcomingCount > 0 && (
                                <div className="analytics-item upcoming">
                                    <span className="analytics-label">Due Soon:</span>
                                    <span className="analytics-value">{tagAnalytics.upcomingCount}</span>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                    {userIsManager && (
                        <div className="project-header-actions">
                            <button
                                onClick={() => navigate(`/projects/${projectId}/activity-history`)}
                                className="activity-history-button"
                                title="View Activity History"
                            >
                                <Activity size={16} />
                                Activity History
                            </button>
                            {activeTab === 'tasks' && (
                                <button
                                    onClick={() => setShowTaskForm(true)}
                                    className="button primary create-task-button"
                                >
                                    Create Task
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="project-tabs">
                <button
                    className={`project-tab ${activeTab === 'tasks' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tasks')}
                >
                    <BarChart size={16} />
                    Tasks
                </button>
                <button
                    className={`project-tab ${activeTab === 'billing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('billing')}
                >
                    <DollarSign size={16} />
                    Billing
                </button>
                <button
                    className={`project-tab ${activeTab === 'releases' ? 'active' : ''}`}
                    onClick={() => setActiveTab('releases')}
                >
                    <Package size={16} />
                    Releases
                </button>
                <button
                    className={`project-tab ${activeTab === 'notes-artifacts' ? 'active' : ''}`}
                    onClick={() => setActiveTab('notes-artifacts')}
                >
                    <FileText size={16} />
                    Notes & Artifacts
                </button>
            </div>

            {/* Billing Tab Content */}
            {activeTab === 'billing' && (
                <BillingTab 
                    projectId={projectId}
                    projectName={project?.name}
                    users={users}
                />
            )}

            {/* Release Management Stats Tab Content */}
            {activeTab === 'releases' && (
                <div>
                    {userIsManager && (
                        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => navigate(`/projects/${projectId}/releases`)}
                                className="button secondary release-management-button"
                            >
                                Release Management
                            </button>
                        </div>
                    )}
                    <ReleaseManagementStatsTab 
                        projectId={projectId}
                        projectName={project?.name}
                        tasks={tasks}
                    />
                </div>
            )}

            {/* Notes & Artifacts Tab Content */}
            {activeTab === 'notes-artifacts' && (
                <ProjectNotesArtifacts 
                    projectId={projectId}
                    project={project}
                />
            )}

            {/* Tasks Tab Content */}
            {activeTab === 'tasks' && (
                <>
            {/* Unified Section Headers Row */}
            <div className="unified-section-headers">
                <div className="unified-header-item" onClick={handleStatsToggle} style={{ cursor: 'pointer' }}>
                    <h3 className="unified-header-title">
                        <BarChart size={16} />
                        Task Overview
                    </h3>
                    <button 
                        className="unified-header-toggle"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStatsToggle();
                        }}
                        aria-label={statsCollapsed ? "Expand" : "Collapse"}
                    >
                        {statsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className="unified-header-item" onClick={handleFeatureTagStatsToggle} style={{ cursor: 'pointer' }}>
                    <h3 className="unified-header-title">
                        <BarChart size={16} />
                        Feature Tag Stats
                    </h3>
                    <button 
                        className="unified-header-toggle"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleFeatureTagStatsToggle();
                        }}
                        aria-label={featureTagStatsCollapsed ? "Expand" : "Collapse"}
                    >
                        {featureTagStatsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className="unified-header-item" onClick={handleTaskTimelineToggle} style={{ cursor: 'pointer' }}>
                    <h3 className="unified-header-title">
                        <BarChart size={16} />
                        Task-Feature Timeline
                    </h3>
                    <button 
                        className="unified-header-toggle"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleTaskTimelineToggle();
                        }}
                        aria-label={taskTimelineCollapsed ? "Expand" : "Collapse"}
                    >
                        {taskTimelineCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
            </div>

            {/* Task Statistics */}
            <div className={`task-statistics ${statsCollapsed ? 'section-collapsed' : ''}`}>
                <div className={`stats-grid ${statsCollapsed ? 'collapsed' : ''}`}>
                    <div className="stat-card stat-open" title={`${taskStats.open} Open Tasks`}>
                        <div className="stat-icon">
                            <AlertCircle size={18} strokeWidth={2} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-number">{taskStats.open}</div>
                            <div className="stat-label">Open</div>
                        </div>
                    </div>
                    <div className="stat-card stat-in-progress" title={`${taskStats.inProgress} In Progress Tasks`}>
                        <div className="stat-icon">
                            <Clock size={18} strokeWidth={2} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-number">{taskStats.inProgress}</div>
                            <div className="stat-label">In Progress</div>
                        </div>
                    </div>
                    <div className="stat-card stat-completed" title={`${taskStats.completed} Completed Tasks`}>
                        <div className="stat-icon">
                            <CheckCircle size={18} strokeWidth={2} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-number">{taskStats.completed}</div>
                            <div className="stat-label">Completed</div>
                        </div>
                    </div>
                    <div className="stat-card stat-total" title={`${taskStats.total} Total Tasks`}>
                        <div className="stat-icon">
                            <BarChart size={18} strokeWidth={2} />
                        </div>
                        <div className="stat-content">
                            <div className="stat-number">{taskStats.total}</div>
                            <div className="stat-label">Total</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Feature Tag Stats */}
            <div className={`feature-tag-stats-wrapper ${featureTagStatsCollapsed ? 'section-collapsed' : ''}`}>
                <FeatureTagStats 
                    featureTags={featureTags}
                    tasks={tasks}
                    isCollapsed={featureTagStatsCollapsed}
                    onToggleCollapse={handleFeatureTagStatsToggle}
                />
            </div>

            {/* Task-Feature Timeline */}
            <div className={`task-feature-timeline-wrapper ${taskTimelineCollapsed ? 'section-collapsed' : ''}`}>
                <TaskFeatureTimeline 
                    featureTags={featureTags}
                    tasks={tasks}
                    onTaskClick={openTaskSummaryModal}
                    isCollapsed={taskTimelineCollapsed}
                    onToggleCollapse={handleTaskTimelineToggle}
                />
            </div>

            {/* Tag Bar */}
            <div className="tag-bar-section">
                <div className="tag-bar-header" onClick={() => setTagBarCollapsed(!tagBarCollapsed)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1 }}>
                        <h3 className="tag-bar-title">
                            <Tag size={16} />
                            Feature Tags
                        </h3>
                        {tagBarCollapsed && selectedTagId !== null && (
                            <span className="section-badge">
                                {getSelectedTagName()}
                            </span>
                        )}
                    </div>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setTagBarCollapsed(!tagBarCollapsed);
                        }}
                        aria-label={tagBarCollapsed ? "Expand tags" : "Collapse tags"}
                    >
                        {tagBarCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`tag-bar-wrapper ${tagBarCollapsed ? 'collapsed' : ''}`}>
                    <TagBar
                        tags={featureTags}
                        selectedTagId={selectedTagId}
                        onTagSelect={setSelectedTagId}
                        onTagCreate={handleTagCreate}
                        onTagUpdate={handleFeatureTagUpdate}
                        onTagDelete={handleTagDelete}
                        projectId={projectId}
                        untaggedCount={untaggedCount}
                        tasks={tasks}
                        users={users}
                    />
                </div>
            </div>

            {/* Filters and Controls */}
            <div className="task-controls-section">
                <div className="task-controls-header" onClick={() => setTaskControlsCollapsed(!taskControlsCollapsed)} style={{ cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, flexWrap: 'wrap' }}>
                        <h3 className="task-controls-title">
                            <Filter size={16} />
                            Filters & Controls
                        </h3>
                        {taskControlsCollapsed && getActiveFilters().length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
                                {getActiveFilters().map((filter, index) => (
                                    <span key={index} className="section-badge">
                                        {filter.value}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <button 
                        className="stats-toggle-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            setTaskControlsCollapsed(!taskControlsCollapsed);
                        }}
                        aria-label={taskControlsCollapsed ? "Expand controls" : "Collapse controls"}
                    >
                        {taskControlsCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                    </button>
                </div>
                <div className={`task-controls ${taskControlsCollapsed ? 'collapsed' : ''}`}>
                    <div className="controls-left">
                        <div className="view-toggle">
                            <IconButton
                                icon={<Grid size={16} />}
                                variant={viewMode === 'grid' ? 'brand-primary' : 'neutral-tertiary'}
                                onClick={() => setViewMode('grid')}
                                title="Grid View"
                                aria-pressed={viewMode === 'grid'}
                            />
                            <IconButton
                                icon={<List size={16} />}
                                variant={viewMode === 'list' ? 'brand-primary' : 'neutral-tertiary'}
                                onClick={() => setViewMode('list')}
                                title="List View"
                                aria-pressed={viewMode === 'list'}
                            />
                            <IconButton
                                icon={<Layout size={16} />}
                                variant={viewMode === 'compact' ? 'brand-primary' : 'neutral-tertiary'}
                                onClick={() => setViewMode('compact')}
                                title="Compact View"
                                aria-pressed={viewMode === 'compact'}
                            />
                            {viewMode === 'compact' && (
                                <IconButton
                                    icon={<Circle size={16} />}
                                    variant={subtaskBubblesMode ? 'brand-primary' : 'neutral-tertiary'}
                                    onClick={() => setSubtaskBubblesMode(!subtaskBubblesMode)}
                                    title="Subtask Bubbles"
                                    aria-pressed={subtaskBubblesMode}
                                />
                            )}
                        </div>
                        <div className="sort-control">
                            <IconButton
                                icon={<TrendingDown size={16} />}
                                variant="neutral-tertiary"
                                onClick={toggleSortOrder}
                                title={`Sort ${sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}`}
                            />
                            <span className="sort-label">
                                {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                            </span>
                        </div>
                        <div className="group-toggle-control">
                            <button
                                className={`group-mode-btn ${groupingMode === 'none' ? 'active' : ''}`}
                                onClick={() => setGroupingMode('none')}
                                title="Ungrouped"
                            >
                                <List size={14} />
                                <span>Ungrouped</span>
                            </button>
                            <button
                                className={`group-mode-btn ${groupingMode === 'feature-tag' ? 'active' : ''}`}
                                onClick={() => setGroupingMode('feature-tag')}
                                title="Group by Feature Tags"
                            >
                                <Tag size={14} />
                                <span>By Features</span>
                            </button>
                            <button
                                className={`group-mode-btn ${groupingMode === 'work-plan' ? 'active' : ''}`}
                                onClick={() => setGroupingMode('work-plan')}
                                title="Group by Work Plan"
                            >
                                <CheckCircle size={14} />
                                <span>PickedUp Today</span>
                            </button>
                            <button
                                className={`group-mode-btn ${groupingMode === 'releases' ? 'active' : ''}`}
                                onClick={() => setGroupingMode('releases')}
                                title="Group by Releases"
                            >
                                <Package size={14} />
                                <span>By Releases</span>
                            </button>
                            <button
                                className={`group-mode-btn ${groupingMode === 'task-links' ? 'active' : ''}`}
                                onClick={() => setGroupingMode('task-links')}
                                title="Group by Task Links"
                            >
                                <Link size={14} />
                                <span>By Links</span>
                            </button>
                        </div>
                    </div>
                    <div className="controls-right">
                        <div className="filter-controls">
                            <div className="filter-group">
                                <label htmlFor="filter-status" className="filter-label">
                                    <span className="filter-label-text">Status</span>
                                </label>
                                <div className="filter-select-wrapper status-multiselect-wrapper" ref={statusDropdownRef}>
                                    <div 
                                        className="status-multiselect-trigger"
                                        onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                setStatusDropdownOpen(false);
                                            }
                                        }}
                                    >
                                        <span className="status-multiselect-display">
                                            {filters.status && filters.status.length > 0
                                                ? `${filters.status.length} selected`
                                                : 'All Statuses'}
                                        </span>
                                        <ChevronDown size={14} className={`status-arrow ${statusDropdownOpen ? 'open' : ''}`} />
                                    </div>
                                    {statusDropdownOpen && (
                                        <div className="status-multiselect-dropdown">
                                            {statusOptions.map(option => {
                                                const isSelected = filters.status && filters.status.includes(option.value);
                                                return (
                                                    <label 
                                                        key={option.value}
                                                        className="status-multiselect-option"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => handleStatusToggle(option.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                        <span>{option.label}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="filter-group">
                                <label htmlFor="filter-created-by" className="filter-label">
                                    <span className="filter-label-text">Created By</span>
                                </label>
                                <div className="filter-select-wrapper">
                                    <select
                                        id="filter-created-by"
                                        value={filters.createdBy}
                                        onChange={(e) => handleFiltersChange({ ...filters, createdBy: e.target.value })}
                                        className="filter-select"
                                    >
                                        <option value="">All Users</option>
                                        {filteredUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.display_name || user.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="filter-group">
                                <label htmlFor="filter-assigned-to" className="filter-label">
                                    <span className="filter-label-text">Assigned To</span>
                                </label>
                                <div className="filter-select-wrapper">
                                    <select
                                        id="filter-assigned-to"
                                        value={filters.assignedTo}
                                        onChange={(e) => handleFiltersChange({ ...filters, assignedTo: e.target.value })}
                                        className="filter-select"
                                    >
                                        <option value="">All Users</option>
                                        <option value="unassigned">Unassigned</option>
                                        {filteredUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.display_name || user.email}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="filter-group">
                                <label htmlFor="filter-task-type" className="filter-label">
                                    <span className="filter-label-text">Task Type</span>
                                </label>
                                <div className="filter-select-wrapper">
                                    <TaskTypeSelect
                                        id="filter-task-type"
                                        value={filters.taskType}
                                        onChange={(e) => handleFiltersChange({ ...filters, taskType: e.target.value })}
                                        className="filter-select"
                                        includeAllOption
                                    />
                                </div>
                            </div>
                            {groupingMode !== 'task-links' && (
                                <div className="filter-group filter-group-checkbox">
                                    <label className="filter-label filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={filters.withLinksOnly || false}
                                            onChange={(e) => handleFiltersChange({ ...filters, withLinksOnly: e.target.checked })}
                                            className="filter-checkbox"
                                        />
                                        <span className="filter-label-text">Only tasks with links</span>
                                    </label>
                                </div>
                            )}
                            {((filters.status && filters.status.length > 0) || filters.createdBy || filters.assignedTo || filters.taskType || filters.withLinksOnly || selectedTagId) && (
                                <button
                                    className="clear-filters-btn"
                                    onClick={clearFilters}
                                    title="Clear all filters"
                                >
                                    <span>Clear</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Search Box */}
            <div className="task-search-section">
                <div className="task-search-wrapper">
                    <Search size={18} className="task-search-icon" />
                    <input
                        type="text"
                        className="task-search-input"
                        placeholder="Search tasks by title, description, ID, assignee, or tag..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            className="task-search-clear"
                            onClick={() => setSearchQuery('')}
                            title="Clear search"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {/* Filter Summary */}
            {(filters.status || filters.createdBy || filters.assignedTo || filters.taskType || filters.withLinksOnly || selectedTagId || searchQuery) && (
                <div className="filter-summary">
                    <span className="filter-summary-text">
                        Showing {filteredAndSortedTasks.length} of {tasks.length} tasks
                    </span>
                </div>
            )}

            {/* Floating Create Task Button */}
            {userIsManager && activeTab === 'tasks' && (
                <button
                    onClick={() => setShowTaskForm(true)}
                    className={`floating-create-task-button ${showTaskForm ? 'hidden' : ''}`}
                    aria-label="Create Task"
                    title="Create Task"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>
            )}

            {showTaskForm && (
                <>
                    <div className="task-form-backdrop" />
                    <div className="task-form-side-panel">
                        <div className="task-form-side-panel-header">
                            <h2>{createSubtaskForTaskId ? 'Create Subtask' : 'Create New Task'}</h2>
                            <button 
                                className="task-form-side-panel-close"
                                onClick={() => { setShowTaskForm(false); setCreateSubtaskForTaskId(null); }}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div className="task-form-side-panel-content">
                            <TaskForm
                                key={createSubtaskForTaskId ?? 'new'}
                                onSuccess={handleTaskCreated}
                                onCancel={() => { setShowTaskForm(false); setCreateSubtaskForTaskId(null); }}
                                users={users}
                                isProjectTask={true}
                                projectId={projectId}
                                onTagCreate={handleTagCreate}
                                availableTags={featureTags}
                                titleInputRef={taskFormTitleRef}
                                defaultTaskType={createSubtaskForTaskId ? TASK_TYPE_SUBTASK : DEFAULT_TASK_TYPE}
                                parentTask={createSubtaskForTaskId ? tasks.find(t => t.id === createSubtaskForTaskId) : null}
                                onSwitchToStandalone={createSubtaskForTaskId ? () => setCreateSubtaskForTaskId(null) : null}
                            />
                        </div>
                    </div>
                </>
            )}

            <TaskList
                tasks={tasksInTreeOrder}
                allTasks={tasks}
                subtaskIds={subtaskIds}
                releases={releases}
                setTasks={setTasks}
                onStatusChange={handleTaskStatusChange}
                onAssigneeChange={handleAssigneeChange}
                users={users}
                isManager={isManager}
                viewMode={viewMode}
                availableTags={featureTags}
                projectId={projectId}
                onTagUpdate={handleTagUpdate}
                onTagCreate={handleTagCreate}
                onTaskExplore={openTaskSummaryModal}
                onAddSubtask={(task) => {
                    setCreateSubtaskForTaskId(task.id);
                    setShowTaskForm(true);
                    setSelectedTaskForModal(null);
                }}
                groupingMode={groupingMode}
                groupedTasks={groupedTasks}
                groupedTasksByWorkPlan={groupedTasksByWorkPlan}
                groupedTasksByReleases={groupedTasksByReleases}
                groupedTasksByLinks={groupedTasksByLinks}
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                isFeatureOverdue={isFeatureOverdue}
                allowCrossProject={project?.allow_cross_project_task_linking || false}
                subtaskBubblesMode={subtaskBubblesMode}
            />

            {/* Task Summary Modal */}
            <TaskSummaryModal
                task={selectedTaskForModal}
                isOpen={!!selectedTaskForModal}
                onClose={closeTaskSummaryModal}
                getTaskStatus={getTaskStatus}
                getStatusVariant={getStatusVariant}
                getTaskHistory={getTaskHistory}
                availableTags={featureTags}
                allTasks={tasks}
                projectId={projectId}
                allowCrossProject={project?.allow_cross_project_task_linking || false}
                isManager={isManager}
                onTaskClick={openTaskSummaryModal}
                onAddSubtask={(task) => {
                    setCreateSubtaskForTaskId(task.id);
                    setShowTaskForm(true);
                    setSelectedTaskForModal(null);
                }}
                onTaskUpdate={(updatedTask) => setTasks(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t))}
                onLinksUpdated={(taskId, links) => {
                    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, linked_tasks: links } : t));
                    setSelectedTaskForModal(prev => prev?.id === taskId ? { ...prev, linked_tasks: links } : prev);
                }}
            />
                </>
            )}
        </div>
    );
};

export default ProjectDetails; 